import { z } from 'zod';

import { validatePhoneByCountry } from '../constants/countryCodes';

/**
 * Password policy (matches mobile registration requirements):
 * - Minimum 9 characters
 * - At least 1 lowercase letter
 * - At least 1 uppercase letter
 * - At least 1 number
 * - At least 1 special character
 */
const passwordSchema = z
  .string()
  .min(9, 'validation.password_min_length')
  .regex(/[a-z]/, 'validation.password_needs_lowercase')
  .regex(/[A-Z]/, 'validation.password_needs_uppercase')
  .regex(/\d/, 'validation.password_needs_number')
  .regex(/[@$!%*?&]/, 'validation.password_needs_special');

export const createUserSchema = z
  .object({
    username: z
      .string()
      .min(6, 'validation.username_min_length')
      .max(50, 'validation.username_max_length')
      .regex(/^[a-zA-Z0-9]+$/, 'validation.username_alphanumeric'),
    displayName: z
      .string()
      .min(2, 'validation.display_name_min_length')
      .max(100, 'validation.display_name_max_length'),
    mobileNumber: z
      .string()
      .regex(/^[0-9]{0,15}$/, 'validation.mobile_format')
      .or(z.literal('')),
    countryCode: z
      .string()
      .regex(/^[0-9]{1,5}$/, 'validation.country_code_format')
      .or(z.literal('')),
    password: passwordSchema,
  })
  .refine(
    (data) => {
      // If no mobile number provided, skip validation
      if (!data.mobileNumber) return true;

      // Validate phone number format based on country code
      return validatePhoneByCountry(
        data.countryCode || '852',
        data.mobileNumber,
      );
    },
    {
      message: 'validation.mobile_invalid_for_country',
      path: ['mobileNumber'],
    },
  );

export const updateUserSchema = z
  .object({
    displayName: z
      .string()
      .min(2, 'validation.display_name_min_length')
      .max(100, 'validation.display_name_max_length')
      .optional(),
    mobileNumber: z
      .string()
      .regex(/^[0-9]{0,15}$/, 'validation.mobile_format')
      .or(z.literal(''))
      .optional(),
    countryCode: z
      .string()
      .regex(/^[0-9]{1,5}$/, 'validation.country_code_format')
      .or(z.literal(''))
      .optional(),
    password: passwordSchema.or(z.literal('')).optional(),
  })
  .refine(
    (data) => {
      if (!data.mobileNumber) return true;

      return validatePhoneByCountry(
        data.countryCode || '852',
        data.mobileNumber,
      );
    },
    {
      message: 'validation.mobile_invalid_for_country',
      path: ['mobileNumber'],
    },
  );

export type ICreateUserFormData = z.infer<typeof createUserSchema>;
export type IUpdateUserFormData = z.infer<typeof updateUserSchema>;
